import { useState } from 'react';

import Box from '@mui/material/Box';
import { DataGrid, GridRowEditStopReasons } from '@mui/x-data-grid';
import { useMediaQuery } from '@mui/material';

import { getProduct } from '../api/api';
import { generateColumns } from './components/columns';
import { ImageModal } from './components/ImageModal';
import CustomToolbar from './components/CustomToolbar';
import { useFetchProducts } from '../hooks/useFetchProducts';
import { style } from './style';
import { ProductVariantModal } from './components/ProductVariantModal/ProductVariantModal';
import { createActionButtons } from './components/ActionButtons';
import { useAppContext } from '../context/AppContext';

export const ProductDashboard = () => {
  const { setIsLoggedIn, rows } = useAppContext();

  const [rowModesModel, setRowModesModel] = useState({});

  const [selectionModel, setSelectionModel] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const { showToast } = useFetchProducts();

  const [openModal, setOpenModal] = useState(false);
  const [openVariantModal, setOpenVariantModal] = useState(false);
  const [modalImages, setModalImages] = useState([]);
  const [productVariant, setProductVariant] = useState([]);

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const handleImageClick = (images) => {
    setModalImages(images);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalImages([]);
    setCurrentImageIndex(0); // Reset to first image
  };

  const handleCloseVariantModal = () => {
    setOpenVariantModal(false);
  };

  const handleCellDoubleClick = (params, event) => {
    // Prevent default double-click edit behavior
    event.defaultMuiPrevented = true;
  };

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id) => () => {
    getProduct(id)
      .then((res) => {
        const currProductVariant = {
          ...res,
          variations: res?.variations?.map((v) => ({ ...v, initial_skroutz_quantity: v.skroutz_quantity })),
        };

        setProductVariant(currProductVariant);
        setOpenVariantModal(true);
      })
      .catch((e) => {
        if (e.status === 401) {
          setIsLoggedIn(false);
          showToast('Unauthorized. Please login again.', 'error');
        } else {
          showToast('An error occurred while fetching product.', 'error');
        }
      });
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const ActionButtons = createActionButtons(handleEditClick);

  const columns = generateColumns(isMobile, ActionButtons, handleImageClick);

  return (
    <Box sx={{ minHeight: '50vh', px: 2 }}>
      <DataGrid
        rows={rows}
        columns={columns}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        onCellDoubleClick={handleCellDoubleClick} // Disable double-click editing
        slots={{
          toolbar: CustomToolbar,
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            quickFilterProps: {
              variant: 'outlined',
            },
            printOptions: { disableToolbarButton: true },
            csvOptions: { disableToolbarButton: true },
          },
        }}
        disableColumnSelector
        disableDensitySelector
        onRowSelectionModelChange={(newRowSelectionModel) => setSelectionModel(newRowSelectionModel)}
        componentsProps={{
          toolbar: {
            sx: { width: '100%' },
            disableExport: true,
          },
        }}
        sx={style}
        pagination
        disableRowSelectionOnClick
        disableMultipleRowSelection
        rowSelectionModel={selectionModel}
      />
      <ImageModal
        openModal={openModal}
        imgIndex={currentImageIndex}
        onCloseModal={handleCloseModal}
        modalImages={modalImages}
        setImgIndex={setCurrentImageIndex}
        editedIds={rowModesModel}
      />
      <ProductVariantModal
        setProductVariant={setProductVariant}
        productVariant={productVariant}
        open={openVariantModal}
        onClose={handleCloseVariantModal}
      />
    </Box>
  );
};
